import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Navigation from "./components/Navigation";
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import SubsidiariesPage from "./pages/SubsidiariesPage";
import ServicesPage from "./pages/ServicesPage";
import ContactPage from "./pages/ContactPage";
import Footer from "./components/Footer";
import "./styles.css";

export default function App() {
  const [theme, setTheme] = useState("light");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mapPathToPage = (path) => {
    const p = (path || "/").replace(/\/+$/, "") || "/";
    if (p === "/") return "home";
    if (p.startsWith("/about")) return "about";
    if (p.startsWith("/subsidiaries")) return "subsidiaries";
    if (p.startsWith("/services")) return "services";
    if (p.startsWith("/contact")) return "contact";
    return "home";
  };

  const [currentPage, setCurrentPage] = useState(() => {
    // initialize from the current URL to avoid a flash of 'home' on first render
    try {
      return mapPathToPage(window.location && window.location.pathname);
    } catch (e) {
      return "home";
    }
  });
  const [showTopBar, setShowTopBar] = useState(true);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
  }, [mobileMenuOpen]);

  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  // Keep a navigateTo helper for older components that expect it
  // We'll use a small wrapper component that has access to router navigate
  function AppWithRouter() {
    const location = useLocation();
    const navigate = useNavigate();

    // Sync currentPage with the URL on mount / when location changes
    useEffect(() => {
      // normalize pathname (remove trailing slash)
      const p = (location.pathname || "/").replace(/\/+$/, "") || "/";
      let page = "home";
      if (p === "/") page = "home";
      else if (p.startsWith("/about")) page = "about";
      else if (p.startsWith("/subsidiaries")) page = "subsidiaries";
      else if (p.startsWith("/services")) page = "services";
      else if (p.startsWith("/contact")) page = "contact";
      setCurrentPage(page);
    }, [location.pathname]);

    const navigateTo = (page) => {
      setCurrentPage(page);
      setMobileMenuOpen(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
      // map page names to routes
      const routeMap = {
        home: "/",
        about: "/about",
        subsidiaries: "/subsidiaries",
        services: "/services",
        contact: "/contact",
      };
      navigate(routeMap[page] || "/");
    };

    const pageVariants = {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    };

    const pageTransition = { duration: 0.35, ease: "easeInOut" };

    return (
      <div className={`app ${theme}`}>
        <Navigation
          theme={theme}
          toggleTheme={toggleTheme}
          currentPage={currentPage}
          navigateTo={navigateTo}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
          showTopBar={showTopBar}
          setShowTopBar={setShowTopBar}
        />

        <main>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial="initial"
              animate="animate"
              exit="exit"
              variants={pageVariants}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <Routes location={location}>
                <Route
                  path="/"
                  element={<HomePage navigateTo={navigateTo} />}
                />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/subsidiaries" element={<SubsidiariesPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/contact" element={<ContactPage />} />
              </Routes>
            </motion.div>
          </AnimatePresence>
        </main>

        <Footer
          navigateTo={(p) => {
            setCurrentPage(p);
            navigateTo(p);
          }}
        />
      </div>
    );
  }

  return (
    <Router>
      <AppWithRouter />
    </Router>
  );
}
